/**
 * PATH: apps/quote/src/components/builder/Skeleton.tsx
 * PURPOSE: Skeleton loading components for premium feel
 */

import { cn } from '@/lib/utils'

// -----------------------------------------------------------------------------
// Base Skeleton
// -----------------------------------------------------------------------------

interface SkeletonProps {
  className?: string
}

export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'animate-pulse rounded bg-slate-200 dark:bg-slate-700',
        className
      )}
    />
  )
}

// -----------------------------------------------------------------------------
// Grid Row Skeleton
// -----------------------------------------------------------------------------

export function GridRowSkeleton() {
  return (
    <div className="h-10 flex items-center gap-2 px-3 border-b border-slate-100 dark:border-slate-800">
      <Skeleton className="w-8 h-4" />
      <Skeleton className="flex-1 h-4 max-w-xs" />
      <Skeleton className="w-16 h-4" />
      <Skeleton className="w-16 h-4" />
      <Skeleton className="w-20 h-4" />
      <Skeleton className="w-20 h-4" />
      <Skeleton className="w-24 h-4" />
    </div>
  )
}

// -----------------------------------------------------------------------------
// Section Card Skeleton
// -----------------------------------------------------------------------------

export function SectionCardSkeleton() {
  return (
    <div className="p-3 border-b border-slate-100 dark:border-slate-800">
      <div className="flex items-center justify-between">
        <Skeleton className="w-32 h-5" />
        <Skeleton className="w-6 h-6 rounded-full" />
      </div>
      <div className="flex items-center gap-4 mt-2">
        <Skeleton className="w-16 h-4" />
        <Skeleton className="w-20 h-4" />
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Builder Page Skeleton
// -----------------------------------------------------------------------------

export function BuilderPageSkeleton() {
  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
      {/* Left drawer skeleton */}
      <div className="w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 flex flex-col">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <Skeleton className="w-24 h-5" />
        </div>
        <div className="flex-1 p-2 space-y-1">
          {[1, 2, 3, 4].map((i) => (
            <SectionCardSkeleton key={i} />
          ))}
        </div>
      </div>
      
      {/* Center area skeleton */}
      <div className="flex-1 flex flex-col">
        {/* Header skeleton */}
        <div className="h-14 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center px-4 gap-4">
          <Skeleton className="w-48 h-6" />
          <Skeleton className="w-20 h-8 rounded-md" />
          <div className="flex-1" />
          <Skeleton className="w-24 h-8 rounded-md" />
          <Skeleton className="w-8 h-8 rounded-full" />
        </div>
        
        {/* Grid header skeleton */}
        <div className="h-10 bg-slate-100 dark:bg-slate-800/50 flex items-center gap-2 px-3 border-b border-slate-200 dark:border-slate-700">
          <Skeleton className="w-8 h-4 opacity-50" />
          <Skeleton className="flex-1 h-4 max-w-xs opacity-50" />
          <Skeleton className="w-16 h-4 opacity-50" />
          <Skeleton className="w-16 h-4 opacity-50" />
          <Skeleton className="w-20 h-4 opacity-50" />
          <Skeleton className="w-20 h-4 opacity-50" />
          <Skeleton className="w-24 h-4 opacity-50" />
        </div>
        
        {/* Grid rows skeleton */}
        <div className="flex-1 bg-white dark:bg-slate-900">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <GridRowSkeleton key={i} />
          ))}
        </div>
        
        {/* Bottom bar skeleton */}
        <div className="h-10 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex items-center px-4 gap-4">
          <Skeleton className="w-32 h-4" />
          <Skeleton className="w-24 h-4" />
          <div className="flex-1" />
          <Skeleton className="w-48 h-4" />
        </div>
      </div>
      
      {/* Right drawer skeleton */}
      <div className="w-80 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 flex flex-col">
        <div className="h-12 border-b border-slate-200 dark:border-slate-700 flex items-center px-4 gap-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="w-16 h-8 rounded-md" />
          ))}
        </div>
        <div className="flex-1 p-4 space-y-4">
          <Skeleton className="w-full h-32 rounded-lg" />
          <Skeleton className="w-full h-24 rounded-lg" />
          <Skeleton className="w-3/4 h-4" />
          <Skeleton className="w-full h-4" />
          <Skeleton className="w-1/2 h-4" />
        </div>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Inline Skeleton (for cells)
// -----------------------------------------------------------------------------

export function CellSkeleton() {
  return <Skeleton className="w-full h-5 rounded" />
}

// -----------------------------------------------------------------------------
// Stats Card Skeleton
// -----------------------------------------------------------------------------

export function StatsCardSkeleton() {
  return (
    <div className="p-4 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
      <Skeleton className="w-24 h-4 mb-2" />
      <Skeleton className="w-32 h-8" />
    </div>
  )
}

